import React, { useState, ChangeEvent } from "react";
import { CreditCard, Banknote, History, Check, Truck } from "lucide-react";
import CommonButtons from "src/shared/components/CommonButtons";
import { Order } from "src/shared/services/interfacev2";
import { toast } from "react-toastify";

interface Props {
  order: Order;
  onUpdateStatus: (status: string) => Promise<boolean>;
  onOpenPayment: (method?: string) => void;
  onOpenCancel: () => void;
}

const OrderActions: React.FC<Props> = ({ order, onUpdateStatus, onOpenPayment, onOpenCancel }) => {
  const currentStatus = order?.orders?.[0]?.status || "Pending";
  const [selectedMethod, setSelectedMethod] = useState<string>("cash");
  const [isUpdatingStatus, setIsUpdatingStatus] = useState<boolean>(false);

  // ✅ Calculate remaining amount
  const remainingAmount = Number(order?.orders?.[order.orders.length - 1]?.remainingAmount ?? 0);

  if (currentStatus === "Cancelled") {
    return (
      <div className="w-full flex justify-center py-3">
        <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-2.5 text-red-700 font-semibold text-sm">This order has been Cancelled.</div>
      </div>
    );
  }

  // Only Confirm and Completed in the dropdown
  const availableOptions: Record<string, string[]> = {
    Pending: ["Pending", "Confirm"],
    Confirm: ["Confirm", "Delivered"], // ✅ Added "Delivered" to Confirm options
    "In-process": ["Confirm"],
    "Waiting for delivery": ["Confirm"],
    Completed: ["Completed"],
    Cancelled: ["Cancelled"],
    Delivered: ["Delivered"], // ✅ Added Delivered
  };

  const handleStatusChange = async (e: ChangeEvent<HTMLSelectElement>) => {
    const newStatus = e.target.value;
    if (newStatus === currentStatus) return;

    setIsUpdatingStatus(true);
    try {
      const success = await onUpdateStatus(newStatus);
      if (success) {
        toast.success(`Order status updated to "${newStatus}"`, {
          style: { borderRadius: "12px", background: "#1e293b", color: "#fff" },
        });
      }
    } catch (err) {
      console.error("Failed to update status", err);
    } finally {
      setIsUpdatingStatus(false);
    }
  };

  // ✅ Show payment methods ONLY when status is "Confirm" AND remaining amount > 0
  const showPaymentMethods = currentStatus === "Confirm" && remainingAmount > 0;

  // ✅ Show balance cleared message when status is "Confirm" AND remaining amount === 0
  const showBalanceCleared = currentStatus === "Confirm" && remainingAmount === 0;

  const methods = [
    {
      key: "buy on credit",
      label: "Buy on Credit",
      icon: History,
      description: "Pay later",
    },
    {
      key: "card",
      label: "Card",
      icon: CreditCard,
      description: "Debit / Credit",
    },
    {
      key: "cash",
      label: "Cash",
      icon: Banknote,
      description: "Pay now",
    },
  ];

  return (
    <div className="w-full space-y-2 bg-white rounded-xl border border-gray-200 p-2">
      {/* Order Status Section */}
      <div>
        <div className="flex items-center gap-2 mb-2">
          <div className="w-1 h-4 bg-slate-900 rounded-full" />
          <h3 className="text-xs font-bold uppercase tracking-wider text-slate-500">Order Status</h3>
        </div>

        <div className="relative">
          <select
            value={currentStatus}
            onChange={handleStatusChange}
            disabled={isUpdatingStatus || currentStatus === "Delivered" || currentStatus === "Cancelled"}
            className="w-full bg-white border border-slate-200 rounded-xl px-3 py-2.5 text-sm font-semibold text-slate-700 outline-none focus:ring-2 focus:ring-slate-900/10 focus:border-slate-900 transition-all appearance-none cursor-pointer disabled:bg-slate-50 disabled:cursor-not-allowed"
          >
            {(availableOptions[currentStatus] || [currentStatus]).map((opt) => (
              <option key={opt} value={opt} className="text-slate-700 font-medium">
                {opt}
              </option>
            ))}
          </select>

          <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none text-slate-400">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M19 9l-7 7-7-7" />
            </svg>
          </div>
        </div>
      </div>

      {/* ✅ Balance Cleared Message — shown when status is "Confirm" AND remainingAmount === 0 */}
      {showBalanceCleared && (
        <div className="flex flex-col items-center justify-center py-4 text-center bg-emerald-50 rounded-xl border border-emerald-200">
          {/* Animated Green Circle */}
          <div className="relative flex items-center justify-center mb-2">
            <div className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center animate-scaleIn">
              <svg className="w-7 h-7 text-emerald-600 animate-drawCheck" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={3} strokeLinecap="round" strokeLinejoin="round">
                <path d="M5 13l4 4L19 7" />
              </svg>
            </div>
          </div>

          {/* Text */}
          <h4 className="text-base font-semibold text-emerald-700">Order Balance Fully Cleared</h4>
          <p className="text-sm sm:text-base text-gray-600 mt-1">
            You have no remaining balance for this order. <br /> Please change the order status to <b className="text-emerald-700">"Delivered"</b>.
          </p>
        </div>
      )}

      {/* ✅ Payment Methods Section — shown ONLY when status is "Confirm" AND remainingAmount > 0 */}
      {showPaymentMethods && (
        <div>
          <div className="flex items-center gap-2">
            <div className="w-1 h-4 bg-slate-900 rounded-full" />
            <h3 className="text-xs font-bold uppercase tracking-wider text-slate-500">Payment Method</h3>
          </div>

          <div className="rounded-xl p-2 bg-gray-100 border border-gray-200">
            <div className="grid grid-cols-3 gap-2">
              {methods.map((method) => {
                const Icon = method.icon;
                const isActive = selectedMethod === method.key;

                return (
                  <button
                    key={method.key}
                    onClick={() => setSelectedMethod(method.key)}
                    className={`relative rounded-lg border p-2 transition-all duration-200 text-left
                      ${isActive ? "bg-slate-900 text-white border-transparent shadow-sm" : "bg-white border-gray-200 hover:border-indigo-300"}`}
                  >
                    {isActive && (
                      <div className="absolute top-1 right-1 w-3.5 h-3.5 flex items-center justify-center rounded-full bg-white/30">
                        <Check size={8} className="text-white" />
                      </div>
                    )}

                    <div className="flex items-center gap-2">
                      <div
                        className={`w-7 h-7 flex items-center justify-center rounded-full flex-shrink-0
                        ${isActive ? "bg-white/20 text-white" : "bg-gray-100 text-gray-600"}`}
                      >
                        <Icon size={14} />
                      </div>
                      <div className="flex flex-col leading-tight min-w-0">
                        <span className={`text-xs font-semibold truncate ${isActive ? "text-white" : "text-gray-800"}`}>{method.label}</span>
                        <span className={`text-[10px] truncate ${isActive ? "text-white/80" : "text-gray-500"}`}>{method.description}</span>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>

            <div className="mt-3">
              <CommonButtons className="h-9 w-full" label="Complete Payment" onClick={() => onOpenPayment(selectedMethod)} />
            </div>
          </div>
        </div>
      )}

      {/* Cancel Button - Updated variant to match your CommonButtons */}
      {currentStatus !== "Delivered" && currentStatus !== "Cancelled" && currentStatus !== "Completed" && (
        <div className="flex justify-end pt-1">
          <CommonButtons className="h-9 w-36" label="Cancel Order" variant="danger" onClick={onOpenCancel} />
        </div>
      )}
    </div>
  );
};

export default OrderActions;
